// -----------------------------------------------------------------------------
// Http Client (Axios)
// -----------------------------------------------------------------------------
//
// -----------------------------------------------------------------------------
// TO USE THIS SERVICE
// (Instantiate an instance for your base URL)
// -----------------------------------------------------------------------------
//
// import HttpService from '../services/http'
//
// const http = new HttpService({
//   debug: true|false, // for debug logging
//   baseUrl: <the_api_base_url>
// })
//
// -----------------------------------------------------------------------------
// AVAILABLE FUNCTIONS
// -----------------------------------------------------------------------------
//
// http.get(uri, options)
// http.post(uri, options)
// http.put(uri, options)
// http.delete(uri, options)
//
// Where the options object supports a spread of name/value pairs for a query string request:
// options = { param: value, param: value, ... }
//
// To send the name/value pairs as body data with a POST or PUT request, nest in the "bodyData" property:
// options.bodyData = { param: value, param: value, ... }
//
// -----------------------------------------------------------------------------
// EXAMPLE USAGE
// -----------------------------------------------------------------------------
//
// GET request:
//
// const uri = '/orders'
// const params = { with: 'orderDetails' }
// const payload = await http.get(uri, params)
//
// POST request:
//
// const uri = '/location'
// const bodyData = { name: 'New Shipper', brand_name: 'kamazai herbal' }
// const payload = await http.post(uri, { bodyData })
//
// -----------------------------------------------------------------------------
import axios from 'axios'

const defaultLogNamespace = '[HTTP]'

export default class HttpService {
  constructor (options) {
    this.debug = options.debug
    this.logNamespace = options.logNamespace || defaultLogNamespace
    this.baseUrl = options.baseUrl || ''
    this.timeout = options.timeout || 30 * 1000

    // Instantiate HTTP client
    this.http = axios.create({
      baseURL: this.baseUrl,
      timeout: this.timeout
    })
  }

  get (uri, opts = {}) {
    opts.method = 'GET'
    return this.performRequest(uri, opts)
  }

  post (uri, opts = {}) {
    opts.method = 'POST'
    return this.performRequest(uri, opts)
  }

  put (uri, opts = {}) {
    opts.method = 'PUT'
    return this.performRequest(uri, opts)
  }

  delete (uri, opts = {}) {
    opts.method = 'DELETE'
    return this.performRequest(uri, opts)
  }

  performRequest (uri, opts = {}) {
    const { method, bodyData, ...otherParams } = opts
    const { headers, ...params } = otherParams
    const requestOpts = { params, headers }
    const data = bodyData || null
    if (method === 'DELETE') requestOpts.data = data

    if (this.debug) {
      console.log(`${this.logNamespace} Provided options =>`, opts)
      console.log(`${this.logNamespace} Executing request ${method} ${uri}`)
      console.log(`${this.logNamespace} with request opts =>`, requestOpts)
      if (data) console.log(`${this.logNamespace} with body data =>`, data)
    }

    let request = null
    switch (method) {
      case 'GET': request = this.http.get(uri, requestOpts); break
      case 'POST': request = this.http.post(uri, data, requestOpts); break
      case 'PUT': request = this.http.put(uri, data, requestOpts); break
      case 'DELETE': request = this.http.delete(uri, requestOpts); break
      default: request = this.http.get(uri, requestOpts)
    }

    return request
      .then(payload => payload.data)
      .catch((error) => {
        if (error.response && error.response.status) {
          if (this.debug) console.log(`${this.logNamespace} Error =>`, error.response)
          throw (error.response.data)
        }
        // Endpoint is unreachable
        if (error.request) {
          const noResponseError = { name: 'Network Error', status: 503, text: 'The network is unreachable' }
          if (this.debug) console.log(`${this.logNamespace} Network Error =>`, noResponseError)
          throw (noResponseError)
        }
        if (this.debug) console.log(`${this.logNamespace} Error =>`, error.message)
        throw error
      })
  }
}
